import { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Keeps a tab / view selection in the URL query string so the selection is
 * shareable and survives a refresh or a browser back-forward.
 *
 * Conventions this hook enforces for every caller:
 * - The state is *derived* from the URL, never mirrored into `useState`. There
 *   is no sync effect to get out of step, and a pasted link renders the right
 *   tab on the very first paint rather than flashing the default.
 * - An unknown or absent value falls back to `defaultValue`, so a hand-edited
 *   or stale link degrades to the default tab instead of a blank page.
 * - The default selection omits the param entirely, which keeps the canonical
 *   URL of a page clean (`/admin-app/finances`, not `?tab=invoices`).
 * - Writes use `replace: true`: back should mean "the page I came from", not
 *   "the tab I was just on".
 *
 * Other query params on the URL are preserved across a tab switch, so this
 * composes with search/filter params that a page may also serialize.
 */
export function useUrlTabState<T extends string>(
  key: string,
  values: readonly T[],
  defaultValue: T,
): [T, (next: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const raw = searchParams.get(key);
  // The null check is what makes the cast honest: matching on `raw ?? ''` alone would
  // hand back `raw` — i.e. null — for a page whose value list legitimately contains ''.
  const active = raw !== null && (values as readonly string[]).includes(raw) ? (raw as T) : defaultValue;

  // react-router rebuilds `setSearchParams` whenever the URL changes, so route
  // it through a latest-ref to keep our own setter referentially stable —
  // callers can then drop it into a dependency array without re-firing effects
  // on every tab switch. The functional update form reads the live params at
  // commit time, so a one-render-old reference is still correct.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  const setActive = useCallback(
    (next: T) => {
      setSearchParamsRef.current(
        prev => {
          const params = new URLSearchParams(prev);
          if (next === defaultValue) params.delete(key);
          else params.set(key, next);
          return params;
        },
        { replace: true },
      );
    },
    [key, defaultValue],
  );

  return [active, setActive];
}
